<template lang="pug">
  div(v-if="tabConfig" v-loading="loading")
    el-row
      el-col
        h3.title {{ tabConfig.title }}
    el-form(
      :model="form"
      ref="formRef"
      :rules="validationRules"
      :inline="true"
      label-position="top"
      size="mini"
    )
      template(v-if="isSubsidiary")
        el-row
          el-col
            el-form-item(label="ID Interno:" prop="internal_id")
              el-input(v-model="form.internal_id")
            el-form-item(label="Nombre:" prop="name")
              el-input(v-model="form.name")
            el-form-item(label="Currency ID:" prop="currency_id")
              el-input(v-model="form.currency_id")
      el-row(
        v-for="(row, rowIndex) in accountRows"
        :key="rowIndex"
        :gutter="24"
        class="account-row"
      )
        el-col(
          v-for="(account, colIndex) in row"
          :key="account.key"
          :span="row.length > 1 ? 12 : 24"
          :class="{ 'account-col--right': colIndex === 1 }"
        )
          h4 {{ account.label }}
          el-form-item(label="ID Interno:" :prop="account.key + '.internal_id'")
            el-input(v-model="form[account.key].internal_id")
          el-form-item(label="N° de Cuenta:" :prop="account.key + '.acct_number'")
            el-input(v-model="form[account.key].acct_number")
          el-form-item(label="Nombre de Cuenta:" :prop="account.key + '.acct_name'")
            el-input(v-model="form[account.key].acct_name")
      el-row
        el-col.filters__buttons-wrapper
          el-button(type="default" :disabled="!hasChanges" @click.prevent="cancelUpdate()") Cancelar
          el-button(
            v-if="canSeeButton()"
            type="primary"
            :disabled="!hasChanges"
            :loading="submitLoading"
            @click.prevent="submitForm()") Guardar
</template>

<style lang="scss" scoped>
.account-row {
  margin-bottom: 12px;
}
.account-col--right {
  border-left: 2px solid #b3b8c2;
  padding-left: 24px !important;
}
.el-button--primary.is-disabled {
  background-color: #c0c4cc !important;
  border-color: #c0c4cc !important;
  color: #fff !important;
  &:hover {
    background-color: #c0c4cc !important;
    border-color: #c0c4cc !important;
  }
}
</style>

<script>
// eslint-disable-next-line import/no-cycle
import FinanceApi from '@/services/apiService';
import store from '@/store';
import permissions from '@/constants/permissions';
import { NETSUITE_TABS } from '@/constants/netsuiteTabs';

export default {
  name: 'NetsuiteAccountsTab',
  props: {
    tabKey: { type: String, required: true },
  },
  data() {
    return {
      settingId: null,
      netsuiteFull: {},
      form: {},
      pristineForm: {},
      loading: false,
      submitLoading: false,
    };
  },
  computed: {
    tabConfig() {
      return NETSUITE_TABS.find((tab) => tab.key === this.tabKey);
    },
    isSubsidiary() {
      return this.tabKey === 'subsidiary';
    },
    accountRows() {
      if (!this.tabConfig || !this.tabConfig.accounts) return [];
      const rows = [];
      for (let i = 0; i < this.tabConfig.accounts.length; i += 2) {
        rows.push(this.tabConfig.accounts.slice(i, i + 2));
      }
      return rows;
    },
    hasChanges() {
      return JSON.stringify(this.form) !== JSON.stringify(this.pristineForm);
    },
    validationRules() {
      const requiredRule = (message) => [{ required: true, message, trigger: 'blur' }];
      if (!this.tabConfig) return {};
      const rules = this.isSubsidiary ? {
        internal_id: requiredRule('Por favor ingrese el ID Interno'),
        name: requiredRule('Por favor ingrese el nombre'),
        currency_id: requiredRule('Por favor ingrese el Currency ID'),
      } : {};
      (this.tabConfig.accounts || []).forEach((acc) => {
        rules[`${acc.key}.internal_id`] = requiredRule('Por favor ingrese el ID Interno');
        rules[`${acc.key}.acct_number`] = requiredRule('Por favor ingrese el N° de Cuenta');
        rules[`${acc.key}.acct_name`] = requiredRule('Por favor ingrese el nombre de Cuenta');
      });
      return rules;
    },
  },
  watch: {
    tabKey: {
      immediate: true,
      handler() {
        this.fetchNetsuiteSettings();
      },
    },
  },
  methods: {
    emptyForm() {
      const form = this.isSubsidiary ? { internal_id: '', name: '', currency_id: '' } : {};
      (this.tabConfig.accounts || []).forEach((acc) => {
        form[acc.key] = {
          internal_id: '',
          acct_number: acc.acctNumber || '',
          acct_name: acc.acctName || '',
        };
      });
      return form;
    },
    fetchNetsuiteSettings() {
      this.loading = true;
      FinanceApi.get_general_settings().then((data) => {
        const row = data.body.find((item) => item.netsuite);
        this.settingId = row ? row.id : null;
        this.netsuiteFull = (row && row.netsuite) ? row.netsuite : {};
        const saved = this.netsuiteFull[this.tabKey];
        this.form = this.mergeWithDefaults(saved);
        this.pristineForm = JSON.parse(JSON.stringify(this.form));
        this.loading = false;
      }).catch(() => { this.loading = false; });
    },
    // Fills in any account added to this tab's config after the settings were last saved, so the
    // form never binds to a missing key (saved data may predate a newly added account/customer).
    // Keys already in `saved` that aren't in the current config are kept as-is, so saving the form
    // doesn't drop them.
    mergeWithDefaults(saved) {
      const defaults = this.emptyForm();
      if (!saved) return defaults;

      const merged = { ...defaults };
      Object.keys(saved).forEach((key) => {
        const value = saved[key];
        merged[key] = (value && typeof value === 'object') ? { ...(merged[key] || {}), ...value } : value;
      });
      return merged;
    },
    submitForm() {
      const { formRef } = this.$refs;
      formRef.validate((valid) => {
        if (valid) {
          this.confirmUpdate();
        }
      });
    },
    confirmUpdate() {
      this.$confirm('¿Confirma guardar los cambios en la configuración de Netsuite?', 'Confirmar guardado', {
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        type: 'warning',
      }).then(() => {
        this.updateNetsuiteSettings();
      }).catch(() => {});
    },
    updateNetsuiteSettings() {
      this.startSubmitLoading();
      const payload = { ...this.netsuiteFull, [this.tabKey]: this.form };
      FinanceApi.updateNetsuiteSettings(this.settingId, payload).then(() => {
        this.$notify({
          title: 'Correcto',
          message: 'Configuración modificada correctamente',
          type: 'success',
        });
        this.fetchNetsuiteSettings();
        this.stopSubmitLoading();
      }).catch((exception) => {
        this.$notify.error({ title: 'Error', message: exception.body.message });
        this.stopSubmitLoading();
      });
    },
    cancelUpdate() {
      this.fetchNetsuiteSettings();
    },
    startSubmitLoading() {
      this.submitLoading = true;
    },
    stopSubmitLoading() {
      this.submitLoading = false;
    },
    canSeeButton() {
      const role = store.state.user.role || '';
      return permissions.FinanceGroup.includes(role);
    },
  },
};
</script>
